// src/components/property/ImageProcessingStep.tsx
/**
 * ImageProcessingStep Component
 *
 * Main container for Step 4: Image Processing in the property video wizard.
 * Orchestrates thumbnail grid, controls, lightbox, and original images dialog.
 */

import { useState, useReducer, useCallback } from 'react';
import { useMutation, useQuery, useAction } from 'convex/react';
import { Loader2, AlertCircle } from 'lucide-react';
import { api } from '../../../convex/_generated/api';
import { Button } from '@/components/ui/button';
import { ImageThumbnailGrid } from './ImageThumbnailGrid';
import { ImageProcessingControls } from './ImageProcessingControls';
import { ImageLightbox } from './ImageLightbox';
import { OriginalImagesDialog } from './OriginalImagesDialog';
import type {
  IImageProcessingStepProps,
  IImageAspectRatio,
  IImageSceneTypeInfo,
  IImageProcessingProgress,
} from '@/@types/property';

type ImageProcessingUIState = {
  isProcessing: boolean;
  lightboxIndex: number | null;
  isOriginalDialogOpen: boolean;
  processingError: string | null;
};

type ImageProcessingUIAction =
  | { type: 'START_PROCESSING' }
  | { type: 'PROCESSING_SUCCESS' }
  | { type: 'PROCESSING_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'OPEN_LIGHTBOX'; payload: number }
  | { type: 'CLOSE_LIGHTBOX' }
  | { type: 'SET_ORIGINAL_DIALOG'; payload: boolean };

const initialProcessingUIState: ImageProcessingUIState = {
  isProcessing: false,
  lightboxIndex: null,
  isOriginalDialogOpen: false,
  processingError: null,
};

function processingUIReducer(
  state: ImageProcessingUIState,
  action: ImageProcessingUIAction
): ImageProcessingUIState {
  switch (action.type) {
    case 'START_PROCESSING':
      return { ...state, isProcessing: true, processingError: null };
    case 'PROCESSING_SUCCESS':
      return { ...state, isProcessing: false };
    case 'PROCESSING_ERROR':
      return { ...state, isProcessing: false, processingError: action.payload };
    case 'CLEAR_ERROR':
      return { ...state, processingError: null };
    case 'OPEN_LIGHTBOX':
      return { ...state, lightboxIndex: action.payload };
    case 'CLOSE_LIGHTBOX':
      return { ...state, lightboxIndex: null };
    case 'SET_ORIGINAL_DIALOG':
      return { ...state, isOriginalDialogOpen: action.payload };
  }
}

function ImageProcessingStep({
  property,
  onContinue,
  onBack,
  isContinueLoading = false,
}: IImageProcessingStepProps) {
  const [selectedAspectRatio, setSelectedAspectRatio] = useState<IImageAspectRatio>('9:16');
  const [ui, dispatch] = useReducer(processingUIReducer, initialProcessingUIState);

  // Queries
  const imageData = useQuery(api.property.queries.getPropertyImages, {
    propertyId: property._id,
  });

  // Mutations
  const excludeImage = useMutation(api.property.mutations.excludeImage);
  const restoreImage = useMutation(api.property.mutations.restoreImage);
  const updateImageOrder = useMutation(api.property.mutations.updateImageOrder);

  // Actions
  const processImages = useAction(api.property.imageProcessing.processImages);

  // Derived state
  const imageIds = imageData?.imageIds ?? property.imageIds;
  const excludedIndices = imageData?.excludedImageIndices ?? [];
  const sceneTypes: IImageSceneTypeInfo[] = (imageData?.imageSceneTypes ??
    []) as IImageSceneTypeInfo[];

  // Get processed images for current aspect ratio
  const processedImagesForRatio =
    selectedAspectRatio === '9:16'
      ? imageData?.processedImages9x16
      : imageData?.processedImages16x9;

  const hasProcessedImages = (processedImagesForRatio?.length ?? 0) > 0;
  const showPlaceholders = !hasProcessedImages;

  // Calculate active (non-excluded) image count for placeholders
  const activeImageCount = imageIds.length - excludedIndices.length;

  // Processing progress from real-time updates
  const progress: IImageProcessingProgress | undefined = imageData?.imageProcessingProgress as
    | IImageProcessingProgress
    | undefined;

  // Handlers
  const handleExclude = useCallback(
    async (index: number) => {
      await excludeImage({ propertyId: property._id, imageIndex: index });
    },
    [excludeImage, property._id]
  );

  const handleRestore = useCallback(
    async (index: number) => {
      await restoreImage({ propertyId: property._id, imageIndex: index });
    },
    [restoreImage, property._id]
  );

  const handleReorder = useCallback(
    async (newImageIds: typeof imageIds) => {
      await updateImageOrder({ propertyId: property._id, newImageIds });
    },
    [updateImageOrder, property._id]
  );

  const handleProcess = useCallback(async () => {
    dispatch({ type: 'START_PROCESSING' });
    try {
      const result = await processImages({
        propertyId: property._id,
        aspectRatio: selectedAspectRatio,
      });
      if (result.error) {
        dispatch({ type: 'PROCESSING_ERROR', payload: result.error });
      } else {
        dispatch({ type: 'PROCESSING_SUCCESS' });
      }
    } catch (error) {
      dispatch({
        type: 'PROCESSING_ERROR',
        payload: 'An unexpected error occurred while processing images.',
      });
      console.error('Image processing error:', error);
    }
  }, [processImages, property._id, selectedAspectRatio]);

  const handleImageClick = useCallback((index: number) => {
    dispatch({ type: 'OPEN_LIGHTBOX', payload: index });
  }, []);

  const handleLightboxClose = useCallback(() => {
    dispatch({ type: 'CLOSE_LIGHTBOX' });
  }, []);

  const handleShowOriginalImages = useCallback(() => {
    dispatch({ type: 'SET_ORIGINAL_DIALOG', payload: true });
  }, []);

  // Validation
  const canContinue = activeImageCount > 0;

  return (
    <div className="w-full max-w-4xl space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Process Images</h2>
        <p className="text-muted-foreground">
          Process images for watermark removal and aspect ratio conversion. Click &quot;Original
          Images&quot; to reorder or exclude.
        </p>
      </div>

      {/* Controls */}
      <ImageProcessingControls
        selectedAspectRatio={selectedAspectRatio}
        isProcessing={ui.isProcessing}
        progress={progress}
        onAspectRatioChange={setSelectedAspectRatio}
        onProcess={handleProcess}
        onShowOriginalImages={handleShowOriginalImages}
      />

      {/* Error Display */}
      {ui.processingError && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive">
          <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium">Processing Error</p>
            <p className="text-sm mt-1 opacity-90">{ui.processingError}</p>
          </div>
          <button
            onClick={() => dispatch({ type: 'CLEAR_ERROR' })}
            className="text-destructive/70 hover:text-destructive"
            aria-label="Dismiss error"
          >
            ×
          </button>
        </div>
      )}

      {/* Image Grid - Shows processed images or placeholders */}
      <ImageThumbnailGrid
        imageIds={imageIds}
        sceneTypes={sceneTypes}
        excludedIndices={excludedIndices}
        aspectRatio={selectedAspectRatio}
        processedImageIds={processedImagesForRatio}
        showPlaceholders={showPlaceholders}
        placeholderCount={activeImageCount}
        onImageClick={handleImageClick}
      />

      {/* Navigation */}
      <div className="flex justify-between pt-4 border-t">
        <Button variant="outline" onClick={onBack} disabled={isContinueLoading}>
          Back
        </Button>
        <Button
          onClick={() => onContinue(selectedAspectRatio)}
          disabled={!canContinue || isContinueLoading}
        >
          {isContinueLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Creating Video...
            </>
          ) : (
            'Continue'
          )}
        </Button>
      </div>

      {/* Original Images Dialog */}
      <OriginalImagesDialog
        open={ui.isOriginalDialogOpen}
        onOpenChange={open => dispatch({ type: 'SET_ORIGINAL_DIALOG', payload: open })}
        imageIds={imageIds}
        excludedIndices={excludedIndices}
        onExclude={handleExclude}
        onRestore={handleRestore}
        onReorder={handleReorder}
      />

      {/* Lightbox - uses processed images when available */}
      <ImageLightbox
        imageIds={processedImagesForRatio ?? []}
        initialIndex={ui.lightboxIndex ?? 0}
        isOpen={ui.lightboxIndex !== null && hasProcessedImages}
        onClose={handleLightboxClose}
      />
    </div>
  );
}

export { ImageProcessingStep };
